import { HdGateGuessingMode } from './gate-guessing-game/hd-gate-guessing-game.service';
import { HdChannelGuessingMode } from './channel-guessing-game/hd-channel-guessing-game.service';
import { HdGateInfoOpenBehavior } from './gate-info/hd-gate-info-requested-context.model';
import { HdContentLanguage } from './hd-content-language.service';
import { HdBodygraphChartPreset, HdPlanetGridVisualizationMode } from './hd-bodygraph.models';
import {
  HdBaseRotation,
  HdBeneathTheLineStyle,
  HdGateNumberAlign,
  HdGateNumberFontSize,
  HdNodeBackgroundStyle,
  HdUndefinedActivationStyle,
} from './planet-grid/hd-planet-grid-color-config';
import {
  HdBodygraphActiveGateColorPreset,
  HdBodygraphCenterBorderStyle,
} from './hd-bodygraph-color-config';
import { HdMoonTransitConnectorStyle, HdMoonTransitGateMarkerStyle } from '../mandala/moon-transit/hd-moon-transit-view.models';
import { HdJustNowTransitColorMode } from '../mandala/just-now-transit/hd-just-now-transit';
import { HdMandalaQuarterColorVariant, HdMandalaZodiacRingVariant } from '../mandala/mandala.constants';

/**
 * Variant-id-union over alle game-types. Toevallig zijn de string-literals
 * voor Poortenjacht en Kanalenjacht identiek (`numbers`/`keynotes`/`mixed`),
 * maar dit blijft een union zodat toekomstige games hun eigen variant-ids
 * kunnen toevoegen zonder breaking change.
 */
export type HdChartGameVariantId = HdGateGuessingMode | HdChannelGuessingMode;
export type HdChartGameId = 'gate-guessing' | 'channel-guessing' | 'draw-chart';

export interface HdChartGameVariantOption {
  readonly id: HdChartGameVariantId;
  /** i18n-sleutel; resolve via de settings-taal (`t(labelKey)`) in de consumer. */
  readonly labelKey: string;
}

export interface HdChartGameOption {
  readonly id: HdChartGameId;
  /** i18n-sleutel; resolve via de settings-taal (`t(labelKey)`) in de consumer. */
  readonly labelKey: string;
  readonly variants: readonly HdChartGameVariantOption[];
}

/**
 * Payload van de `playClicked`-output: bundelt zowel de gekozen game als de
 * gekozen variant zodat de pagina de juiste game-service kan starten met de
 * juiste variant. `sortedGates`, indien aanwezig, geeft een geordende sub-set
 * poorten op om gestructureerd te oefenen (bv. enkel poort 10..20 op
 * volgorde) bij memory-keynote. `targetPreset`, indien aanwezig (enkel bij
 * "Teken je chart"), is de volledig geladen doel-chart die de speler moet
 * natekenen.
 */
export interface HdChartGamePlayRequest {
  readonly gameId: HdChartGameId;
  readonly variantId: HdChartGameVariantId;
  readonly sortedGates?: readonly number[];
  readonly targetPreset?: HdBodygraphChartPreset;
}

export const HD_CHART_GAME_OPTIONS: readonly HdChartGameOption[] = [
  {
    id: 'gate-guessing',
    labelKey: 'humanDesign.chart.options.game.gateGuessing',
    variants: [
      { id: 'numbers', labelKey: 'humanDesign.chart.options.gameVariant.numbers' },
      { id: 'keynotes', labelKey: 'humanDesign.chart.options.gameVariant.keynotes' },
      { id: 'mixed', labelKey: 'humanDesign.chart.options.gameVariant.mixed' },
      { id: 'memory-poort', labelKey: 'humanDesign.chart.options.gameVariant.memoryPoort' },
      { id: 'memory-keynote', labelKey: 'humanDesign.chart.options.gameVariant.memoryKeynote' },
    ],
  },
  {
    id: 'channel-guessing',
    labelKey: 'humanDesign.chart.options.game.channelGuessing',
    variants: [
      { id: 'keynotes', labelKey: 'humanDesign.chart.options.gameVariant.keynotes' },
      { id: 'numbers', labelKey: 'humanDesign.chart.options.gameVariant.numbers' },
      { id: 'memory', labelKey: 'humanDesign.chart.options.gameVariant.memory' },
    ],
  },
  {
    id: 'draw-chart',
    labelKey: 'humanDesign.chart.options.game.drawChart',
    variants: [],
  },
];

export interface HdContentLanguageOption {
  readonly id: HdContentLanguage;
  /** Taal-endoniem (bv. "Nederlands"/"English") — bewust niet vertaald. */
  readonly label: string;
}

export const HD_CONTENT_LANGUAGE_OPTIONS: readonly HdContentLanguageOption[] = [
  { id: 'nl', label: 'Nederlands' },
  { id: 'en', label: 'English' },
];

/** Generiek (id, labelKey)-paar voor de stijl-dropdowns in controls-UI. */
export interface HdChartControlOption<T extends string> {
  readonly id: T;
  /** i18n-sleutel; resolve via de settings-taal (`t(labelKey)`) in de consumer. */
  readonly labelKey: string;
}

export const HD_UNDEFINED_STYLE_OPTIONS: readonly HdChartControlOption<HdUndefinedActivationStyle>[] = [
  { id: 'opacity-border', labelKey: 'humanDesign.chart.options.undefinedStyle.opacityBorder' },
  { id: 'corner-notch', labelKey: 'humanDesign.chart.options.undefinedStyle.cornerNotch' },
  { id: 'hatch-overlay', labelKey: 'humanDesign.chart.options.undefinedStyle.hatchOverlay' },
  { id: 'hatch-notch-border', labelKey: 'humanDesign.chart.options.undefinedStyle.hatchNotchBorder' },
  { id: 'hatch-border', labelKey: 'humanDesign.chart.options.undefinedStyle.hatchBorder' },
  { id: 'notch-border', labelKey: 'humanDesign.chart.options.undefinedStyle.notchBorder' },
  { id: 'border-only', labelKey: 'humanDesign.chart.options.undefinedStyle.borderOnly' },
  { id: 'inverse', labelKey: 'humanDesign.chart.options.undefinedStyle.inverse' },
  { id: 'inverse-border', labelKey: 'humanDesign.chart.options.undefinedStyle.inverseBorder' },
  { id: 'inverse-border-hatch', labelKey: 'humanDesign.chart.options.undefinedStyle.inverseBorderHatch' },
  { id: 'none', labelKey: 'humanDesign.chart.options.undefinedStyle.none' },
];

export const HD_NODE_BACKGROUND_STYLE_OPTIONS: readonly HdChartControlOption<HdNodeBackgroundStyle>[] = [
  { id: 'side-color', labelKey: 'humanDesign.chart.options.nodeBackgroundStyle.sideColor' },
  { id: 'classic-red', labelKey: 'humanDesign.chart.options.nodeBackgroundStyle.classicRed' },
];

export const HD_BENEATH_THE_LINE_STYLE_OPTIONS: readonly HdChartControlOption<HdBeneathTheLineStyle>[] = [
  { id: 'modern', labelKey: 'humanDesign.chart.options.beneathTheLineStyle.modern' },
  { id: 'classic', labelKey: 'humanDesign.chart.options.beneathTheLineStyle.classic' },
];

/**
 * Weergavemodi van de uitgebreide planeetgrid (PHS color/tone-blokken):
 * - classic: enkel de cijfers
 * - extended: 6 indicatoren (kleur-, variant- + cognitie-icoon + toon-schaal)
 * - modern: 4 indicatoren (iconen met cijfer-overlay)
 * - keynotes: classic-vormen + kleine tekst-keynotes
 */
export const HD_PLANET_GRID_VISUALIZATION_MODE_OPTIONS: readonly HdChartControlOption<HdPlanetGridVisualizationMode>[] = [
  { id: 'classic', labelKey: 'humanDesign.chart.options.planetGridVisualizationMode.classic' },
  { id: 'extended', labelKey: 'humanDesign.chart.options.planetGridVisualizationMode.extended' },
  { id: 'modern', labelKey: 'humanDesign.chart.options.planetGridVisualizationMode.modern' },
  { id: 'keynotes', labelKey: 'humanDesign.chart.options.planetGridVisualizationMode.keynotes' },
];

/** Verbindingslijnen in de maan-transit modus (spec 2026-07-06 §6.2/§6.5). */
export const HD_MOON_TRANSIT_CONNECTOR_STYLE_OPTIONS: readonly HdChartControlOption<HdMoonTransitConnectorStyle>[] = [
  { id: 'curved', labelKey: 'humanDesign.chart.options.moonTransitConnectorStyle.curved' },
  { id: 'straight', labelKey: 'humanDesign.chart.options.moonTransitConnectorStyle.straight' },
];

/** Markering van de maanpoorten in het wiel (finetune-ronde 3). */
export const HD_MOON_TRANSIT_GATE_MARKER_STYLE_OPTIONS: readonly HdChartControlOption<HdMoonTransitGateMarkerStyle>[] = [
  { id: 'ring', labelKey: 'humanDesign.chart.options.moonTransitGateMarkerStyle.ring' },
  { id: 'frame', labelKey: 'humanDesign.chart.options.moonTransitGateMarkerStyle.frame' },
];

export const HD_JUST_NOW_TRANSIT_COLOR_MODE_OPTIONS: readonly HdChartControlOption<HdJustNowTransitColorMode>[] = [
  { id: 'two-color', labelKey: 'humanDesign.chart.options.justNowTransitColorMode.twoColor' },
  { id: 'three-color', labelKey: 'humanDesign.chart.options.justNowTransitColorMode.threeColor' },
];

/** Gefadede centerkleur in de mandala-slices van geactiveerde poorten. */
export type HdMandalaSliceCenterFadeMode = 'on' | 'off';

export const HD_MANDALA_SLICE_CENTER_FADE_OPTIONS: readonly HdChartControlOption<HdMandalaSliceCenterFadeMode>[] = [
  { id: 'on', labelKey: 'humanDesign.chart.options.mandalaSliceCenterFade.on' },
  { id: 'off', labelKey: 'humanDesign.chart.options.mandalaSliceCenterFade.off' },
];

/** Kleurvariant van de mandala-kwartaalband (A/B/C-vergelijk, 2026-08-20). */
export const HD_MANDALA_QUARTER_COLOR_VARIANT_OPTIONS: readonly HdChartControlOption<HdMandalaQuarterColorVariant>[] = [
  { id: 'soft', labelKey: 'humanDesign.chart.options.mandalaQuarterColorVariant.soft' },
  { id: 'vivid', labelKey: 'humanDesign.chart.options.mandalaQuarterColorVariant.vivid' },
  { id: 'light', labelKey: 'humanDesign.chart.options.mandalaQuarterColorVariant.light' },
];

export const HD_MANDALA_ZODIAC_RING_VARIANT_OPTIONS: readonly HdChartControlOption<HdMandalaZodiacRingVariant>[] = [
  { id: 'dark', labelKey: 'humanDesign.chart.options.mandalaZodiacRingVariant.dark' },
  { id: 'light', labelKey: 'humanDesign.chart.options.mandalaZodiacRingVariant.light' },
];

export const HD_GATE_NUMBER_ALIGN_OPTIONS: readonly HdChartControlOption<HdGateNumberAlign>[] = [
  { id: 'left', labelKey: 'humanDesign.chart.options.gateNumberAlign.left' },
  { id: 'right', labelKey: 'humanDesign.chart.options.gateNumberAlign.right' },
];

export const HD_GATE_NUMBER_FONT_SIZE_OPTIONS: readonly HdChartControlOption<HdGateNumberFontSize>[] = [
  { id: 'large', labelKey: 'humanDesign.chart.options.gateNumberFontSize.large' },
  { id: 'medium', labelKey: 'humanDesign.chart.options.gateNumberFontSize.medium' },
];

export const HD_BASE_ROTATION_OPTIONS: readonly HdChartControlOption<HdBaseRotation>[] = [
  { id: 'lps', labelKey: 'humanDesign.chart.options.baseRotation.lps' },
  { id: 'classic', labelKey: 'humanDesign.chart.options.baseRotation.classic' },
];

export const HD_CENTER_BORDER_STYLE_OPTIONS: readonly HdChartControlOption<HdBodygraphCenterBorderStyle>[] = [
  { id: 'none', labelKey: 'humanDesign.chart.options.centerBorderStyle.none' },
  { id: 'thin', labelKey: 'humanDesign.chart.options.centerBorderStyle.thin' },
  { id: 'medium', labelKey: 'humanDesign.chart.options.centerBorderStyle.medium' },
  { id: 'thick', labelKey: 'humanDesign.chart.options.centerBorderStyle.thick' },
];

export const HD_ACTIVE_GATE_COLOR_PRESET_OPTIONS: readonly HdChartControlOption<HdBodygraphActiveGateColorPreset>[] = [
  { id: 'source', labelKey: 'humanDesign.chart.options.activeGateColorPreset.source' },
  { id: 'black', labelKey: 'humanDesign.chart.options.activeGateColorPreset.black' },
  { id: 'dynamic', labelKey: 'humanDesign.chart.options.activeGateColorPreset.dynamic' },
];

/** Brugberekening: volledige (advanced) priority-sortering vs. basis. */
export type HdAdvancedBridgeMode = 'on' | 'off';

export const HD_ADVANCED_BRIDGE_OPTIONS: readonly HdChartControlOption<HdAdvancedBridgeMode>[] = [
  { id: 'on', labelKey: 'humanDesign.chart.options.advancedBridge.on' },
  { id: 'off', labelKey: 'humanDesign.chart.options.advancedBridge.off' },
];

/** Tonen/verbergen van het informatieve Chiron-item in de eenvoudige planeetgrid. */
export type HdChironDisplayMode = 'on' | 'off';

export const HD_CHIRON_DISPLAY_OPTIONS: readonly HdChartControlOption<HdChironDisplayMode>[] = [
  { id: 'on', labelKey: 'humanDesign.chart.options.chironDisplay.on' },
  { id: 'off', labelKey: 'humanDesign.chart.options.chironDisplay.off' },
];

/** Weergavestijl van brugpoort-nummers in de Primaire-conditionering rij. */
export type HdBridgeGateStyle = 'badge' | 'dot';

export const HD_BRIDGE_GATE_STYLE_OPTIONS: readonly HdChartControlOption<HdBridgeGateStyle>[] = [
  { id: 'badge', labelKey: 'humanDesign.chart.options.bridgeGateStyle.badge' },
  { id: 'dot', labelKey: 'humanDesign.chart.options.bridgeGateStyle.dot' },
];

// Enkel de twee gedockte varianten zijn nog kiesbaar (2026-09-20): undocken doet
// de gebruiker voortaan zelf, behalve wanneer de app-logica zelf iets undockt
// opent. De oude 'small'/'full' blijven in het type voor oude opgeslagen waarden
// (die worden bij het laden gecoërceerd naar 'full-docked-keep').
export const HD_GATE_INFO_OPEN_BEHAVIOR_OPTIONS: readonly HdChartControlOption<HdGateInfoOpenBehavior>[] = [
  { id: 'full-docked-keep', labelKey: 'humanDesign.chart.options.gateInfoOpenBehavior.fullDockedKeep' },
  { id: 'full-docked-autoclose', labelKey: 'humanDesign.chart.options.gateInfoOpenBehavior.fullDockedAutoclose' },
];
